import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { useNavigate } from "react-router-dom";
import SelectableBoxCommon from "../SelectableBoxCommon";
import NameAndPronounsFields from "../NameAndPronounsFields";
import EmailPasswordFields from "../EmailPasswordFields";
import BiographyField from "../BiographyField";
import DegreesField from "../DegreesField";
import CertificationsField from "../CertificationsField";
import LicenseUploaded from "../LicenseUploaded";
import InsuranceUploaded from "../InsuranceUploaded";
import ManageSocialAccountsFields from "../ManageSocialAccountsFields";
import { ConfirmModal } from "components";
import { useAuth } from "context/authContext";
import { UserService } from "services/UserService";
import { IUser } from "types";
import CircularProgress from "@mui/material/CircularProgress";

type SelectedValueType =
  | "nameAndPronouns"
  | "emailPassword"
  | "biography"
  | "degrees"
  | "certifications"
  | "license"
  | "insurance"
  | "manageSocialAccounts";

const ContentCreatorSettingData = () => {
  const { currentUser, setCurrentUser } = useAuth();
  const [selectedValue, setSelectedValue] =
    useState<SelectedValueType>("nameAndPronouns");
  const [currentStep, setCurrentStep] = useState(0);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [isConfirmModalUpdate, setConfirmModalUpdate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showIcons, setShowIcons] = useState({
    nameAndPronouns: false,
    emailPassword: false,
    biography: false,
    degrees: false,
    certifications: false,
    license: false,
    insurance: false,
    manageSocialAccounts: false,
  });

  const [namePronounsData, setNamePronounsData] = useState({
    firstName: "",
    lastName: "",
    pronouns: "",
    profileUrl: "",
  });
  const [emailPasswordData, setEmailPasswordData] = useState({
    email: "",
    password: "",
  });
  const [biographyData, setBiographyData] = useState({
    biography: "",
  });
  const [degreesData, setDegreesData] = useState({
    degrees: "",
  });
  const [certificationsData, setCertificationsData] = useState({
    certifications: "",
  });
  const [licenseData, setLicenseData] = useState({
    typeOfLicense: "",
    licenseExpiration: "",
    licenseNumber: "",
    licenseImageUrl: "",
    stateofLicensure: "",
  });
  const [insuranceData, setInsuranceData] = useState({
    insuranceNumber: "",
    insuranceExpiration: "",
    insuranceImageUrl: "",
  });
  const [socialsData, setSocialsData] = useState({
    instagram: "",
    linkedin: "",
    personalWebsite: "",
    twitter: "",
    facebook: "",
    other: "",
  });

  // Fetch the user's data from your backend or context (this is a mock function)
  const fetchUserData = async () => {
    setLoading(true);
    try {
      const userData = await UserService.getUserByid(currentUser.uid);
      setNamePronounsData({
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        pronouns: userData.pronouns || "",
        profileUrl: userData.profileUrl || "",
      });
      setEmailPasswordData({
        email: userData?.email || "",
        password: "",
      });
      setBiographyData({
        biography: userData?.biography || "",
      });
      setDegreesData({
        degrees: userData.degrees || "",
      });
      setCertificationsData({
        certifications: userData.certifications || "",
      });
      setLicenseData({
        typeOfLicense: userData.typeOfLicense || "",
        stateofLicensure: userData.stateofLicensure || "",
        licenseExpiration: userData.licenseExpiration || "",
        licenseNumber: userData.licenseNumber || "",
        licenseImageUrl: userData.licenseImageUrl || "",
      });
      setInsuranceData({
        insuranceNumber: userData.insuranceNumber || "",
        insuranceExpiration: userData.insuranceExpiration || "",
        insuranceImageUrl: userData.insuranceImageUrl || "",
      });
      setSocialsData({
        instagram: userData.instagram || "",
        linkedin: userData.linkedin || "",
        personalWebsite: userData.personalWebsite || "",
        twitter: userData.twitter || "",
        facebook: userData.facebook || "",
        other: userData.other || "",
      });

      updateShowIcons(userData);
    } catch (error) {
      console.error("Failed to fetch user data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchUserData();
    }
  }, [currentUser]);

  const handleOpenModalUpdate = () => {
    setConfirmModalUpdate(true);
  };

  const handleConfirmUpdate = async () => {
    setLoading(true);
    try {
      const data = {
        ...namePronounsData,
        ...emailPasswordData,
        ...biographyData,
        ...degreesData,
        ...certificationsData,
        ...licenseData,
        ...insuranceData,
        ...socialsData,
      };

      await UserService.updateUser(currentUser.uid, data);
      setCurrentUser({ ...currentUser, ...data });
    } catch (error) {
      console.error("Failed to update user:", error);
    } finally {
      console.log("User updated successfully");
    }
    setLoading(false);
    setConfirmModalUpdate(false);
  };

  const updateShowIcons = (data: IUser) => {
    setShowIcons({
      nameAndPronouns: !!(data.firstName || data.lastName || data.pronouns),
      emailPassword: !!data.email,
      biography: !!data.biography,
      degrees: data.degrees.length > 0,
      certifications: data?.certifications?.length > 0,
      license: !!data.licenseNumber,
      insurance: !!data.insuranceNumber,
      manageSocialAccounts: data?.socialMediaLinks?.length > 0,
    });
  };

  const handleCloseModalUpdate = () => setConfirmModalUpdate(false);

  const handleNamePronounsChange = (updatedValues: any) => {
    setNamePronounsData(updatedValues);
  };

  const handleEmailPasswordChange = (updatedValues: any) => {
    setEmailPasswordData(updatedValues);
  };

  const handleOpenModal = () => {
    setConfirmModalOpen(true);
  };

  const handleCloseModal = () => setConfirmModalOpen(false);
  const handleConfirm = () => {
    setConfirmModalOpen(false);
  };

  const navigate = useNavigate();

  const allSteps: SelectedValueType[] = [
    "nameAndPronouns",
    "emailPassword",
    "biography",
    "degrees",
    "certifications",
    "license",
    "insurance",
    "manageSocialAccounts",
  ];

  // Condition to skip the first two steps
  const skipFirstTwoSteps = true;
  const steps = skipFirstTwoSteps ? allSteps.slice(0) : allSteps;

  const headers: Record<SelectedValueType, string> = {
    nameAndPronouns: "Name, Pronouns & Profile Image",
    emailPassword: "Email Address & Password",
    biography: "Biography",
    degrees: "Degrees",
    certifications: "Certifications",
    license: "License",
    insurance: "Insurance",
    manageSocialAccounts: "Manage Social Accounts",
  };

  const handleSelectionChange = (value: SelectedValueType) => {
    const stepIndex = allSteps.indexOf(value);
    if (stepIndex !== -1) {
      setCurrentStep(stepIndex);
      setSelectedValue(value);
    }

    setShowIcons((prevState: any) => ({
      ...prevState,
      agreeToTerms: false,
    }));
  };

  const handleNextClick = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setSelectedValue(steps[currentStep + 1]);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        minHeight="100vh"
        mb={2}
        position={"relative"}
      >
        <Box width={{ xs: "100%", md: "21%" }} p={2}>
          <Typography
            variant="h6"
            gutterBottom
            fontSize={18}
            fontWeight={700}
            color={"#595959"}
          >
            User Account
          </Typography>

          <SelectableBoxCommon
            selectedValue={selectedValue}
            value="nameAndPronouns"
            onClick={handleSelectionChange}
            text="Name, Pronouns & Profile Image"
            showIcon={showIcons.nameAndPronouns}
          />

          <Typography
            variant="h6"
            gutterBottom
            mt={2}
            fontSize={18}
            fontWeight={700}
            color={"#595959"}
          >
            Security Settings
          </Typography>

          <SelectableBoxCommon
            selectedValue={selectedValue}
            value="emailPassword"
            onClick={handleSelectionChange}
            text="Email Address & Password"
            showIcon={showIcons.emailPassword}
            disabled={true}
          />

          <Typography
            variant="h6"
            gutterBottom
            mt={2}
            fontSize={18}
            fontWeight={700}
            color={"#595959"}
          >
            Content Creator Account
          </Typography>
          <Box display={"flex"} flexDirection={"column"} gap={2}>
            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="biography"
              onClick={handleSelectionChange}
              text="Biography"
              showIcon={showIcons.biography}
            />
            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="degrees"
              onClick={handleSelectionChange}
              text="Degrees"
              showIcon={showIcons.degrees}
            />
            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="certifications"
              onClick={handleSelectionChange}
              text="Certifications"
              showIcon={showIcons.certifications}
            />
            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="license"
              onClick={handleSelectionChange}
              text="License"
              showIcon={showIcons?.license}
            />
            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="insurance"
              onClick={handleSelectionChange}
              text="Insurance"
              showIcon={showIcons?.insurance}
            />
          </Box>
          <Typography
            variant="h6"
            gutterBottom
            mt={2}
            fontSize={18}
            fontWeight={700}
            color={"#595959"}
          >
            Social Accounts
          </Typography>

          <SelectableBoxCommon
            selectedValue={selectedValue}
            value="manageSocialAccounts"
            onClick={handleSelectionChange}
            text="Manage Social Accounts"
            showIcon={showIcons.manageSocialAccounts}
          />
          <Button sx={styleSheet.saveChangesBtnMobile} type="submit">
            Save Changes
          </Button>
          <Box display="flex" flexDirection="column" mt={3}>
            <Typography
              sx={{
                borderBottom: "2px solid #A9A9A9",
                width: "198px",
              }}
            ></Typography>

            <Button
              sx={styleSheet.saveChangesBtn}
              type="submit"
              onClick={handleOpenModalUpdate}
            >
              Save Changes
            </Button>

            {/* <Box pt={3} ml={3}>
              <Button
                sx={styleSheet.buttonDeactive}
                type="submit"
                onClick={handleOpenModal}
              >
                Deactivate Account
              </Button>
            </Box> */}
            <ConfirmModal
              title="Update Your Profile Data"
              subtitle="Figma ipsum component variant main layer. Line horizontal arrange team editor thumbnail distribute. Follower figma blur connection slice plugin. Text mask hand bullet ipsum slice mask selection bullet list. Polygon align hand bold."
              cancelText="Cancel"
              confirmText="Confirm"
              titleColor="#233F91"
              isLoading={loading}
              onCancel={handleCloseModalUpdate}
              onConfirm={handleConfirmUpdate}
              isConfirmModal={isConfirmModalUpdate}
              icon={
                <ErrorOutlineIcon sx={{ color: "#233F91", fontSize: 40 }} />
              }
            />
            <ConfirmModal
              title="You’re About to Deactivate Your Account"
              subtitle="Figma ipsum component variant main layer. Line horizontal arrange team editor thumbnail distribute. Follower figma blur connection slice plugin. Text mask hand bullet ipsum slice mask selection bullet list. Polygon align hand bold."
              cancelText="Cancel"
              confirmText="Confirm"
              titleColor="#E10A0A"
              isLoading={false}
              onCancel={handleCloseModal}
              onConfirm={handleConfirm}
              isConfirmModal={isConfirmModalOpen}
              icon={
                <ErrorOutlineIcon sx={{ color: "#E10A0A", fontSize: 40 }} />
              }
            />
          </Box>
        </Box>

        <Box
          width={{ xs: "100%", md: "80%" }}
          height={{ xs: "auto", md: "auto" }}
          position={"relative"}
          bgcolor={"#F8F8F8"}
          sx={{
            borderRadius: "10px",
            border: "1px solid #A9A9A9",
            overflow: "hidden",
          }}
        >
          <Box sx={{ marginBottom: "60px" }}>
            <Typography
              variant="h5"
              gutterBottom
              fontSize={18}
              p={3}
              fontWeight={700}
              color={"#797979"}
            >
              {headers[selectedValue]}
            </Typography>
            <Typography
              sx={{
                borderBottom: "1px solid #A9A9A9",
                width: "100%",
              }}
            ></Typography>

            {selectedValue === "nameAndPronouns" ? (
              <NameAndPronounsFields
                onChange={handleNamePronounsChange}
                initialValues={namePronounsData}
                setShowIcons={setShowIcons}
              />
            ) : selectedValue === "emailPassword" ? (
              <EmailPasswordFields
                onChange={handleEmailPasswordChange}
                initialValues={emailPasswordData}
                setShowIcons={setShowIcons}
              />
            ) : selectedValue === "biography" ? (
              <BiographyField
                biographyData={biographyData}
                setBiographyData={setBiographyData}
                setShowIcons={setShowIcons}
              />
            ) : selectedValue === "degrees" ? (
              <DegreesField
                degreesData={degreesData}
                setDegreesData={setDegreesData}
                setShowIcons={setShowIcons}
              />
            ) : selectedValue === "certifications" ? (
              <CertificationsField
                certificationsData={certificationsData}
                setCertificationsData={setCertificationsData}
                setShowIcons={setShowIcons}
              />
            ) : selectedValue === "license" ? (
              <LicenseUploaded
                licenseData={licenseData}
                setLicenseData={setLicenseData}
                // onChange={(updatedLicenseData: any) =>
                //   console.log(updatedLicenseData)
                // }
                setShowIcons={setShowIcons}
                showIcons={showIcons}
              />
            ) : selectedValue === "insurance" ? (
              <InsuranceUploaded
                insuranceData={insuranceData}
                setInsuranceData={setInsuranceData}
                onChange={(updatedInsuranceData: any) =>
                  console.log(updatedInsuranceData)
                }
                setShowIcons={setShowIcons}
              />
            ) : selectedValue === "manageSocialAccounts" ? (
              <ManageSocialAccountsFields
                socialsData={socialsData}
                setSocialsData={setSocialsData}
                setShowIcons={setShowIcons}
              />
            ) : null}
          </Box>

          {selectedValue === "manageSocialAccounts" ? (
            <Box
              position="absolute"
              bottom={0}
              width="100%"
              p={2}
              bgcolor="#F8F8F8"
              display="flex"
              justifyContent={{ xs: "start", md: "center" }}
            >
              <Button
                sx={{
                  color: "#FFFFFF",
                  bgcolor: "#233F91",
                  borderRadius: "10px",
                  border: "1px solid #B3B3B3",
                  fontWeight: 400,
                  fontSize: "15px",
                  ":hover": {
                    bgcolor: "#233F91",
                  },
                }}
                onClick={handleNextClick}
              >
                Next
              </Button>
            </Box>
          ) : (
            <Box
              position="absolute"
              bottom={0}
              width="100%"
              p={2}
              bgcolor="#F8F8F8"
              display="flex"
              justifyContent={{ xs: "start", md: "center" }}
            >
              <Button
                sx={{
                  ...styleSheet.nextBtn,
                  color: showIcons[selectedValue] ? "white" : "#595959",
                  bgcolor: showIcons[selectedValue] ? "#233F91" : "#DBDBDB",
                  "&:hover": {
                    bgcolor: showIcons[selectedValue] ? "#233F91" : "#DBDBDB",
                  },
                }}
                disabled={!showIcons[selectedValue]}
                onClick={handleNextClick}
              >
                Next
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default ContentCreatorSettingData;

const styleSheet = {
  saveChangesBtn: {
    display: { xs: "none", md: "block" },
    mt: 5,
    width: "214px",
    height: "48px",
    p: "8px, 20px, 8px, 20px",
    fontSize: "15px",
    fontWeight: 700,
    backgroundColor: "#233F91",
    borderRadius: "15px",
    color: "white",
    "&:hover": {
      backgroundColor: "#233F91",
      color: "white",
    },
  },
  buttonDeactive: {
    color: "#E10A0A",
    lineHeight: "24px",
    fontWeight: 700,
    fontSize: "15px",
  },
  saveChangesBtnMobile: {
    display: { xs: "block", md: "none" },
    // margingTop: "20px",
    width: "214px",
    height: "48px",
    p: "8px, 20px, 8px, 20px",
    marginTop: "20px",
    fontSize: "15px",
    fontWeight: 700,
    backgroundColor: "#233F91",
    borderRadius: "15px",
    color: "white",
    "&:hover": {
      backgroundColor: "#233F91",
      color: "white",
    },
  },
  textButton: {
    color: "#000000",
    fontWeight: 400,
    fontSize: "17px",
  },
  nextBtn: {
    borderRadius: "10px",
    border: "1px solid #B3B3B3",
    fontWeight: 400,
    fontSize: "15px",
  },
};
